import { refreshSession, signOut } from "@/services/auth";

// Public API endpoints that don't require authentication
const PUBLIC_ENDPOINTS = ["/agreements/questionnaire"];

export interface AuthInterceptorOptions {
  /**
   * Origin API (np. `https://api.dev.redcurrant.eu`). Gdy podany, nagłówki auth trafiają tylko
   * do żądań z tym originem; presigned PUT/GET do S3 wychodzą nietknięte (ASMMNT-83).
   * Bez niego zachowanie sprzed etapu 2 — nagłówki do każdego żądania.
   */
  apiOrigin?: string | null;
}

/** Adres żądania jako URL (string względny — względem strony). */
const getRequestUrl = (request: RequestInfo | URL): URL => {
  if (request instanceof Request) return new URL(request.url);
  if (request instanceof URL) return request;
  return new URL(request, window.location.origin);
};

/**
 * Czy żądanie idzie do API i ma dostać `X-API-key` / `X-Impersonate-Cognito-Id`.
 * Porównujemy pełny origin (schemat + host + port), nie prefiks hosta.
 */
export const shouldAttachAuthHeaders = (
  request: RequestInfo | URL,
  apiOrigin?: string | null,
): boolean => {
  if (!apiOrigin) return true;
  try {
    return getRequestUrl(request).origin === apiOrigin;
  } catch {
    // Nieparsowalny adres na pewno nie jest adresem API.
    return false;
  }
};

const addAPIKey = (
  apiKey: string,
  request: RequestInfo | URL,
  config: RequestInit = {},
  impersonateCognitoId?: string,
) => {
  if (!config.headers) {
    config.headers = {};
  }

  const headers: Record<string, string> = {
    "X-API-key": apiKey,
  };

  if (impersonateCognitoId) {
    headers["X-Impersonate-Cognito-Id"] = impersonateCognitoId;
  }

  Object.assign(config.headers, headers);

  return [request, config] as const;
};

const isPublicEndpoint = (request: RequestInfo | URL): boolean => {
  try {
    const { pathname } = getRequestUrl(request);
    return PUBLIC_ENDPOINTS.some(
      (endpoint) =>
        pathname === endpoint || pathname.startsWith(endpoint + "/"),
    );
  } catch {
    return false;
  }
};

// A refresh that fails for a transient reason (network blip, Cognito 5xx, a
// just-woken tab whose connection is still re-establishing) must not end the
// session — the refresh token is still valid. Only a definitive
// NotAuthorizedException means the refresh token is genuinely invalid/revoked
// and the user has to sign in again (AR-1810).
const REFRESH_MAX_ATTEMPTS = 3;
const REFRESH_BACKOFF_MS = 300;

export const isAuthError = (error: unknown): boolean => {
  const name = (error as { name?: string; code?: string })?.name;
  const code = (error as { name?: string; code?: string })?.code;
  return name === "NotAuthorizedException" || code === "NotAuthorizedException";
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const refreshWithRetry = async (): Promise<string> => {
  let lastError: unknown;
  for (let attempt = 1; attempt <= REFRESH_MAX_ATTEMPTS; attempt++) {
    try {
      const session = await refreshSession();
      return session.getIdToken().getJwtToken();
    } catch (error) {
      lastError = error;
      // Genuine auth failure — retrying cannot help.
      if (isAuthError(error)) {
        throw error;
      }
      if (attempt < REFRESH_MAX_ATTEMPTS) {
        await delay(REFRESH_BACKOFF_MS * attempt);
      }
    }
  }
  throw lastError;
};

let refreshPromise: Promise<string> | null = null;
const getNewAPIKey = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = (async () => {
    try {
      return await refreshWithRetry();
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

export const AuthInterceptor = (
  idToken: string,
  getImpersonateCognitoId?: () => string | null,
  { apiOrigin }: AuthInterceptorOptions = {},
) => {
  // Store the original fetch function
  const originalFetch = window.fetch;

  // Initialize the API key with the provided idToken.
  // It will be updated if the session is refreshed.
  let apiKey = idToken;

  // Override the global fetch function
  window.fetch = async (...args) => {
    const [request, config] = args;

    // Bypass authentication for public endpoints and for anything outside the API
    // (presigned S3 PUT/GET): no auth headers and no 401 → refresh handling.
    if (
      isPublicEndpoint(request) ||
      !shouldAttachAuthHeaders(request, apiOrigin)
    ) {
      return originalFetch(request, config);
    }

    const impersonateCognitoId = getImpersonateCognitoId?.() || undefined;
    const [requestWithAuth, configWithAuth] = addAPIKey(
      apiKey,
      request,
      config,
      impersonateCognitoId,
    );
    const response = await originalFetch(requestWithAuth, configWithAuth);

    // For unauthenticated users, attempt to refresh the session
    if (response.status === 401) {
      try {
        apiKey = await getNewAPIKey();
        const [retryRequest, retryConfig] = addAPIKey(
          apiKey,
          request,
          config,
          impersonateCognitoId,
        );
        const retryResponse = await originalFetch(retryRequest, retryConfig);
        if (retryResponse.status === 401) {
          signOut();
          return retryResponse;
        }
        return retryResponse;
      } catch (refreshError) {
        // Only a definitive auth failure ends the session. A transient failure
        // (network/5xx, exhausted retries) keeps the user signed in and surfaces
        // the original 401 — the next request or focus refetch can recover.
        // Signing out here was the cause of the random multi-tab logouts: one
        // tab's transient refresh failure cleared the shared Cognito storage and
        // cascaded a sign-out across every open tab (AR-1810).
        if (isAuthError(refreshError)) {
          signOut();
        }
        return response;
      }
    }

    // Return the response for successful calls or non-401 errors
    return response;
  };

  // Cleanup function to restore the original fetch
  return () => {
    window.fetch = originalFetch;
  };
};
